import type { CmsSymptomRef } from "@/lib/cms"

/** A biomarker/disease doc that may carry tagged symptoms. */
interface SymptomTagged {
  slug: string
  symptoms?: CmsSymptomRef[] | null
}

/** Extract the searchable text from one populated symptom relationship ref —
 *  its `name` plus any `synonyms` the populate provided. Bare ids (string /
 *  number) and unpopulated refs contribute nothing. */
function aliasesFromRef(ref: CmsSymptomRef): string[] {
  if (!ref || typeof ref === "string" || typeof ref === "number") return []
  const out: string[] = []
  if (typeof ref.name === "string" && ref.name) out.push(ref.name)
  // `synonyms` isn't in the base CmsSymptomRef object shape but may be present
  // when the relationship is populated with more depth — read it defensively.
  const syn = (ref as { synonyms?: unknown }).synonyms
  if (Array.isArray(syn)) {
    for (const s of syn) {
      if (typeof s === "string" && s) out.push(s)
      else if (
        s &&
        typeof s === "object" &&
        typeof (s as { value?: unknown }).value === "string"
      ) {
        out.push((s as { value: string }).value)
      }
    }
  }
  return out
}

/**
 * Build a `slug → symptom alias terms` map from a set of biomarker/disease
 * docs. Each item's tagged symptoms contribute their name (+ synonyms when
 * populated) so a search for a symptom term surfaces the items tagged with it.
 *
 * Returns an empty map when `enabled` is false — the caller passes
 * `areSymptomsEnabled()`, keeping the augmentation inert until launch.
 */
export function buildSymptomAliasMap(
  items: SymptomTagged[],
  enabled: boolean
): Record<string, string[]> {
  if (!enabled) return {}
  const map: Record<string, string[]> = {}
  for (const item of items) {
    if (!item.symptoms?.length) continue
    const aliases = item.symptoms.flatMap(aliasesFromRef)
    if (aliases.length > 0) map[item.slug] = aliases
  }
  return map
}
